import { useEffect, useMemo, useState } from 'react'
import { Building2, FileText, LayoutTemplate, Trash2 } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { AssetOption, PresentationScope, PresentationTemplate, Story } from '../types/presentation.types'
import { AssetPicker } from './AssetPicker'

/**
 * Add a story: blank, or from a template (MYD-505).
 *
 * A template needs an asset, so choosing one asks for it, limited to the
 * template's kind. The server makes the story as the user, leaving out what
 * they may not see (financial slides for a community account, say), and the
 * editor inserts it as one undoable edit. Nothing is saved until the editor
 * saves.
 */

const SCOPE_LABELS: Record<PresentationScope, string> = {
	location: 'Locations',
	project: 'Projects',
	portfolio: 'Portfolios',
	evaluation: 'Evaluations',
}
const SCOPE_ORDER: PresentationScope[] = ['location', 'project', 'portfolio', 'evaluation']

interface AddStoryDialogProps {
	open: boolean
	onClose: () => void
	onBlank: () => void
	/** The made stories; `dropped` counts blocks left out for the user. */
	onInsert: (stories: Story[], dropped: number) => void
	/** "Portfolio and its locations" (MYD-507): hand over to the portfolio deck dialog. */
	onPortfolio?: () => void
}

const BLANK = 'blank'

export function AddStoryDialog({ open, onClose, onBlank, onInsert, onPortfolio }: AddStoryDialogProps) {
	const [templates, setTemplates] = useState<PresentationTemplate[] | null>(null)
	const [chosen, setChosen] = useState<string>(BLANK)
	const [asset, setAsset] = useState<AssetOption | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		setChosen(BLANK)
		setAsset(null)
		setError(null)
		let cancelled = false
		presentationService
			.listTemplates()
			.then(list => !cancelled && setTemplates(list))
			.catch(() => !cancelled && setTemplates([]))
		return () => {
			cancelled = true
		}
	}, [open])

	const template = templates?.find(candidate => candidate.key === chosen) ?? null
	const grouped = useMemo(
		() => SCOPE_ORDER.map(scope => [scope, (templates ?? []).filter(candidate => candidate.scope === scope)] as const).filter(([, list]) => list.length > 0),
		[templates],
	)

	const choose = (key: string) => {
		setChosen(key)
		setError(null)
		// An asset picked for another kind of template does not carry over.
		const next = templates?.find(candidate => candidate.key === key)
		if (asset && next && asset.scope !== next.scope) setAsset(null)
	}

	const submit = async () => {
		if (!template) {
			onBlank()
			return
		}
		if (!asset) return
		setBusy(true)
		setError(null)
		try {
			const made = await presentationService.instantiateTemplate(template.key, [{ scope: asset.scope, entityId: asset.entityId }])
			if (made.stories.length === 0) {
				setError('That asset is not available to you.')
				return
			}
			onInsert(made.stories, made.dropped)
		} catch {
			setError('The story could not be made. Try again.')
		} finally {
			setBusy(false)
		}
	}

	const remove = async (key: string) => {
		try {
			await presentationService.deleteTemplate(key)
			setTemplates(current => (current ?? []).filter(candidate => candidate.key !== key))
			if (chosen === key) setChosen(BLANK)
		} catch {
			setError('That template could not be deleted.')
		}
	}

	const option = (key: string, title: string, detail: string, icon: React.ReactNode, extra?: React.ReactNode) => (
		<div
			key={key}
			className={cn(
				'flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
				chosen === key ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
			)}
		>
			<button type="button" role="radio" aria-checked={chosen === key} onClick={() => choose(key)} className="flex min-w-0 flex-1 items-start gap-3 text-left">
				<span className="mt-0.5 shrink-0 text-gray-500">{icon}</span>
				<span className="min-w-0">
					<span className="block text-sm font-medium text-gray-900">{title}</span>
					<span className="block text-xs text-gray-500">{detail}</span>
				</span>
			</button>
			{extra}
		</div>
	)

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[600px]">
				<DialogHeader>
					<DialogTitle>Add story</DialogTitle>
					<DialogDescription>Start blank, or from a template: pick one and the asset it is about.</DialogDescription>
				</DialogHeader>

				<div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4">
					<div role="radiogroup" aria-label="Story template" className="space-y-4">
						{option(BLANK, 'Blank story', 'An empty story to build slide by slide.', <FileText className="h-4 w-4" />)}
						{onPortfolio && (
							<button
								type="button"
								onClick={onPortfolio}
								className="flex w-full items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-left transition-colors hover:border-gray-300 hover:bg-gray-50"
							>
								<span className="mt-0.5 shrink-0 text-gray-500">
									<Building2 className="h-4 w-4" />
								</span>
								<span className="min-w-0">
									<span className="block text-sm font-medium text-gray-900">Portfolio and its locations</span>
									<span className="block text-xs text-gray-500">An overview story, then one for each location, from templates.</span>
								</span>
							</button>
						)}

						{templates === null && <p className="text-xs text-muted-foreground">Loading templates...</p>}
						{grouped.map(([scope, list]) => (
							<div key={scope} className="space-y-2">
								<p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{SCOPE_LABELS[scope]}</p>
								{list.map(candidate =>
									option(
										candidate.key,
										candidate.name,
										[candidate.description, `${candidate.slideCount} ${candidate.slideCount === 1 ? 'slide' : 'slides'}`, candidate.isStarter ? '' : 'Yours'].filter(Boolean).join(' · '),
										<LayoutTemplate className="h-4 w-4" />,
										candidate.isOwner ? (
											<button
												type="button"
												onClick={() => remove(candidate.key)}
												className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-destructive"
												aria-label={`Delete template ${candidate.name}`}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</button>
										) : undefined,
									),
								)}
							</div>
						))}
					</div>

					{template && (
						<div className="space-y-2 border-t border-gray-200 pt-4">
							<Label>About which {template.scope}?</Label>
							<AssetPicker key={template.scope} value={asset} onChange={setAsset} scopes={[template.scope]} />
							<p className="text-[11px] text-muted-foreground">Slides you do not have access to, such as financials, are left out.</p>
						</div>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={busy}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={busy || (template !== null && !asset)}>
						{busy ? 'Adding...' : 'Add story'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default AddStoryDialog
